import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AuthLayout } from "@/components/Brand";
import { Field, FormError, SubmitButton, LoadingState, ErrorState, StatusBadge } from "@/components/common";
import { useAuth, GARAGE_KEY } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setMe } = useAuth();
  const [inv, setInv] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/auth/invites/${token}`)
      .then(({ data }) => { setInv(data); setForm((f) => ({ ...f, name: data.name || "" })); })
      .catch((e) => setLoadError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!inv.user_exists && form.password !== form.confirm) return setError(t("auth.passwordMismatch"));
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/auth/invites/${token}/accept`, { name: form.name, password: form.password });
      setMe(data);
      const m = data.memberships.find((x) => x.slug === inv.garage_slug && x.role === inv.role);
      if (inv.role === "customer") return navigate(`/g/${inv.garage_slug}/account`, { replace: true });
      if (m) localStorage.setItem(GARAGE_KEY, m.garage_id);
      navigate(inv.role === "garage_manager" ? "/pro/onboarding" : "/pro", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <AuthLayout title={t("invite.title")} subtitle={inv ? t("invite.subtitle", { garage: inv.garage_name }) : ""}>
      {!inv && !loadError && <LoadingState />}
      {loadError && <ErrorState message={loadError} />}
      {inv && (
        <form onSubmit={submit} className="space-y-5" data-testid="accept-invite-form">
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="invite-summary">
            <Building2 className="h-5 w-5 text-teal-700" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-900">{inv.garage_name}</p>
              <p className="truncate text-sm text-slate-600">{inv.email}</p>
            </div>
            <StatusBadge status={inv.role} />
          </div>
          <FormError message={error} />
          {inv.user_exists ? (
            <p className="text-sm text-slate-600" data-testid="invite-existing-account-note">{t("invite.existingAccount")}</p>
          ) : (
            <Field label={t("invite.fullName")} htmlFor="inv-name">
              <Input id="inv-name" required value={form.name} onChange={set("name")} className="h-11" data-testid="invite-name-input" />
            </Field>
          )}
          <Field label={inv.user_exists ? t("auth.password") : t("invite.choosePassword")} htmlFor="inv-pw" hint={inv.user_exists ? null : t("auth.passwordRule")}>
            <Input id="inv-pw" type="password" minLength={8} required value={form.password} onChange={set("password")} className="h-11" data-testid="invite-password-input" />
          </Field>
          {!inv.user_exists && (
            <Field label={t("auth.confirmPassword")} htmlFor="inv-pw2">
              <Input id="inv-pw2" type="password" minLength={8} required value={form.confirm} onChange={set("confirm")} className="h-11" data-testid="invite-password-confirm-input" />
            </Field>
          )}
          <SubmitButton busy={busy} className="h-11 w-full" data-testid="accept-invite-submit-button">{t("invite.accept")}</SubmitButton>
        </form>
      )}
    </AuthLayout>
  );
}
